import React, { useMemo, useState } from 'react';
import { NSCard, NSButton } from 'bricks';
import {
    CardBody, CardHeader, Row, Col,
} from 'reactstrap';
import { downloadFileFromUrl } from 'helpers/file/download';
import ReadOnlyWrapper from 'components/ReadOnlyWrapper/ReadOnlyWrapper';
import useToast, { ToastType } from 'hooks/useToast';
import { IncomeCalculationType } from 'constants/proForma';
import RenderIf from 'components/RenderIf/RenderIf';
import { isEmpty } from 'lodash';
import { useGetProFormaExecutiveSummary } from '../../hooks';
import { getProFormaExecutiveSummaryPdf } from '../../hooks/useGetProFormaExecutiveSummaryPdf';
import {
    formatDevelopment, formatAssumptions, formatForecast,
    formatReturns,
    formatOperations,
} from './helpers';
import ExecutiveSummaryDetail from './ExecutiveSummaryDetail/ExecutiveSummaryDetail';
import ExecutiveSummaryGenericTable from './ExecutiveSummaryGenericTable/ExecutiveSummaryGenericTable';
import ExecutiveSummaryTimelineChart from './ExecutiveSummaryTimelineChart/ExecutiveSummaryTimelineChart';
import { useProFormaSocketContext } from '../ProForma/socketContext/ProFormaSocketProvider';
import { SUPPORTED_EXECUTIVE_SUMMARY_EXPORT_MODELS } from './constants';

interface IProps {
    hasWriteAccessToProForma: boolean;
    dealId: number;
    proFormaId: number;
}

const ExecutiveSummary: React.FC<IProps> = ({ hasWriteAccessToProForma, dealId, proFormaId }) => {
    const [isDownloading, setIsDownloading] = useState(false);
    const { showToast } = useToast();

    const {
        disposition, returnsData, uses, sources, proForma, waterfallAnalysis,
        incomeSummary, operatingInfo, permanentDebtsInfo, expenses, wattsType,
    } = useProFormaSocketContext();
    const permanentDebtInfo = permanentDebtsInfo?.[0];

    const formattedDevelopment = useMemo(
        () => formatDevelopment(proForma.proFormaModelId, uses, sources),
        [proForma.proFormaModelId, uses, sources],
    );
    const formattedAssumptions = useMemo(() => formatAssumptions(
        proForma.proFormaModelId, incomeSummary, operatingInfo, expenses, wattsType,
    ), [
        proForma.proFormaModelId, incomeSummary, operatingInfo, expenses, wattsType,
    ]);
    const formattedForecast = useMemo(
        () => formatForecast(disposition!, operatingInfo, permanentDebtInfo, returnsData),
        [disposition, operatingInfo, permanentDebtInfo, returnsData],
    );
    const formattedOperations = useMemo(() => formatOperations(
        proForma.proFormaModelId, disposition!, operatingInfo?.incomeCalculationType as IncomeCalculationType, wattsType,
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ), [
        proForma.proFormaModelId, disposition, wattsType,
    ]);
    const { data } = useGetProFormaExecutiveSummary(dealId, proFormaId, { enabled: !!proFormaId && !!dealId });

    const formattedReturns = useMemo(() => (
        returnsData && Object.keys(returnsData).length > 0
            ? formatReturns(returnsData, waterfallAnalysis)
            : null
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ), [returnsData]);

    const isExportSupported = SUPPORTED_EXECUTIVE_SUMMARY_EXPORT_MODELS.includes(proForma.proFormaModelId);

    const exportDealOverviewPdf = async () => {
        if (data) {
            setIsDownloading(true);
            const toastId = showToast('Downloading PDF...', ToastType.Success, { autoClose: false });
            try {
                const response = await getProFormaExecutiveSummaryPdf(dealId, proFormaId);
                const pdfUrl = response.url;
                await downloadFileFromUrl(pdfUrl, `${data.general.dealName}-executive-summary`, 'pdf', true);
                showToast(
                    'PDF downloaded successfully!',
                    ToastType.Success,
                    { autoClose: 3000 },
                    toastId,
                );
            } catch {
                showToast(
                    'Failed to download PDF',
                    ToastType.Error,
                    { autoClose: 3000 },
                    toastId,
                );
            } finally {
                setIsDownloading(false);
            }
        }
    };

    return (
        <RenderIf isTrue={!isEmpty(proForma)} isLoading={isEmpty(proForma)}>
            <ReadOnlyWrapper isReadOnly={!hasWriteAccessToProForma}>
                <NSCard className="NSCard--level-2 shadow-none">
                    <CardHeader>
                        <div className="d-flex align-items-center justify-content-between text-white">
                            <div className="d-flex align-items-center">
                                <div>
                                    <h4 className="mb-0">Executive Summary</h4>
                                    <h5 className="mb-1 mt-1 text-muted">{proForma?.name}</h5>
                                </div>
                            </div>
                            <div>
                                <NSButton
                                    id="exec-summary-export-btn"
                                    color="primary"
                                    className="mr-2 heap-deal-pro-forma-exec-summary-export"
                                    outline
                                    text="Export"
                                    callback={exportDealOverviewPdf}
                                    disabled={isDownloading || !isExportSupported}
                                    disabledTooltip={!isExportSupported ? 'Coming soon!' : ''}
                                />
                            </div>
                        </div>
                    </CardHeader>
                    <CardBody>
                        <ExecutiveSummaryDetail />
                        {data && <ExecutiveSummaryTimelineChart data={[data.timelineData]} />}
                        <Row className="mx-n1">
                            <Col className="px-1 mb-2" sm={12} md={5}>
                                <ExecutiveSummaryGenericTable title="ASSUMPTIONS" sections={formattedAssumptions} fixedHeight />
                            </Col>
                            <Col className="px-1 mb-2" sm={12} md={7}>
                                <ExecutiveSummaryGenericTable title="FORECAST" sections={formattedForecast} rowClassName="py-1" fixedHeight />
                            </Col>
                        </Row>
                        <div>
                            {formattedReturns && <ExecutiveSummaryGenericTable title="RETURNS" sections={formattedReturns} />}
                            <div className="mb-2" />
                            {formattedDevelopment && <ExecutiveSummaryGenericTable title="DEVELOPMENT" sections={formattedDevelopment} />}
                            <div className="mb-2" />
                            {formattedOperations && <ExecutiveSummaryGenericTable title="OPERATIONS" sections={formattedOperations} />}
                        </div>
                    </CardBody>
                </NSCard>
            </ReadOnlyWrapper>
        </RenderIf>
    );
};

export default ExecutiveSummary;
